import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import {
  ModalStepConfiguration,
  StepConfig,
  StepIds,
  usePremarketplaceBaseFlow,
} from '~/components/premarketplace/partner-config';
import { PremarketplaceFormDataSchema, usePremarketplaceContext } from '~/provider/PremarketplaceProvider';
import { MarketplacePartner } from '~/types/marketplace';
import { buildStepAnswers, STEP_FIELD_MAP } from '~/utils/premarketplace/step-answers';
import { computeVisibleSteps } from '~/utils/premarketplace/step-flow';

/** StepManager
 *  Manages the current step and navigation between steps.
 */
export interface StepManager {
  currentStepId: StepIds;
  currentStepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  resetSteps: () => void;
}

const useStepManager = (steps: StepConfig[]): StepManager => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();

  const pathnameStepId = pathname.split('premarketplace/')[1] as StepIds | undefined;
  const [currentStepId, setCurrentStepId] = useState<StepIds>(pathnameStepId ?? steps[0]?.id);

  // Keep state in sync when Remix navigates externally (e.g. index → first step redirect)
  // without going through nextStep()/previousStep().
  useEffect(() => {
    if (pathnameStepId) setCurrentStepId(pathnameStepId);
  }, [pathnameStepId]);

  // Derive index from the URL pathname (not `currentStepId` state) so the
  // back-button default and progress bar always reflect the position the
  // user is actually viewing — including on first paint, deep link, and
  // browser-back, where `currentStepId` state may briefly lag the URL.
  const currentStepIndex = useMemo(() => {
    const stepIndex = steps?.findIndex((step) => step.id === pathnameStepId);
    return stepIndex > 0 ? stepIndex : 0;
  }, [steps, pathnameStepId]);

  const previousStepIndex = useMemo(() => {
    return currentStepIndex - 1 >= 0 ? currentStepIndex - 1 : currentStepIndex;
  }, [currentStepIndex]);

  // Advance on the render AFTER the step's answer is written, so a branch step the
  // answer just revealed or removed is already reflected in `steps` when we choose
  // the destination. StepLayout calls `onNext()` (which writes formData) and then
  // `nextStep()` in the same handler; navigating synchronously here would read the
  // stale, pre-answer `steps` and could target a now-hidden step — which
  // useHiddenStepRedirect would then bounce back to the first step (INT-1294).
  const [advanceRequested, setAdvanceRequested] = useState(false);
  const nextStep = useCallback(() => setAdvanceRequested(true), []);

  useEffect(() => {
    if (!advanceRequested) return;
    setAdvanceRequested(false);
    const fromIndex = steps.findIndex((step) => step.id === pathnameStepId);
    const currentIndex = fromIndex >= 0 ? fromIndex : 0;
    const nextStepId = steps[Math.min(currentIndex + 1, steps.length - 1)]?.id;
    if (nextStepId) {
      setCurrentStepId(nextStepId);
      navigate(`/premarketplace/${nextStepId}?${searchParams.toString()}`);
    }
  }, [advanceRequested, steps, pathnameStepId, navigate, searchParams]);

  const previousStep = useCallback(() => {
    const previousStepId = steps[previousStepIndex]?.id;

    setCurrentStepId(previousStepId);
    navigate(-1);
  }, [previousStepIndex, steps, navigate]);

  const goToStep = useCallback(() => {}, []);

  const resetSteps = useCallback(() => {
    const firstStepId = steps[0]?.id;
    setCurrentStepId(firstStepId);
    if (firstStepId) {
      navigate(`/premarketplace/${firstStepId}?${searchParams.toString()}`);
    }
  }, [steps, navigate, searchParams]);

  return {
    currentStepId,
    currentStepIndex,
    totalSteps: steps.length,
    isFirstStep: currentStepIndex === 0,
    isLastStep: currentStepIndex === steps.length - 1,
    nextStep,
    previousStep,
    goToStep,
    resetSteps,
  };
};

// If a changed earlier answer hides the step the URL currently points at, that
// step's route would otherwise still render (and `currentStepIndex` would clamp
// to 0). Redirect to the first visible step so no hidden step is shown.
const useHiddenStepRedirect = (steps: StepConfig[]) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const pathnameStepId = pathname.split('premarketplace/')[1] as StepIds | undefined;

  useEffect(() => {
    if (!pathnameStepId || steps.length === 0) return;
    if (steps.some((step) => step.id === pathnameStepId)) return;

    const firstStepId = steps[0]?.id;
    if (firstStepId && firstStepId !== pathnameStepId) {
      navigate(`/premarketplace/${firstStepId}?${searchParams.toString()}`, { replace: true });
    }
  }, [pathnameStepId, steps, navigate, searchParams]);
};

// When a step transitions shown → hidden, drop its stored answer so a later
// negative clause can't read an answer the buyer can no longer see, and the lead
// payload never carries an orphaned answer. `formData`/`setFormData` are read via
// refs so this only runs when the visible steps actually change (not every render).
const useStaleAnswerCleanup = (steps: StepConfig[]) => {
  const { formData, setFormData } = usePremarketplaceContext();
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const setFormDataRef = useRef(setFormData);
  setFormDataRef.current = setFormData;
  const prevVisibleIdsRef = useRef<StepIds[]>([]);

  useEffect(() => {
    const currentIds = steps.map((step) => step.id);
    const currentIdSet = new Set(currentIds);
    const nowHidden = prevVisibleIdsRef.current.filter((id) => !currentIdSet.has(id));
    prevVisibleIdsRef.current = currentIds;
    if (nowHidden.length === 0) return;

    const currentFormData = formDataRef.current ?? {};

    const fieldsToClear = nowHidden
      .map((id) => STEP_FIELD_MAP[id as keyof typeof STEP_FIELD_MAP])
      .filter((field): field is (typeof STEP_FIELD_MAP)[keyof typeof STEP_FIELD_MAP] => Boolean(field))
      .filter((field) => currentFormData[field] !== undefined);

    if (fieldsToClear.length === 0) return;
    const cleared = Object.fromEntries(fieldsToClear.map((field) => [field, undefined]));
    setFormDataRef.current(cleared as Partial<PremarketplaceFormDataSchema>);
  }, [steps]);
};

/** StepContext */
export interface StepData {
  [key: string]: unknown;
}

interface StepContextType extends StepManager {
  config: ModalStepConfiguration | null;
}

const StepContext = createContext<StepContextType | undefined>(undefined);

interface StepProviderProps {
  children: ReactNode;
  partner?: MarketplacePartner;
}

export const StepProvider = ({ children, partner }: StepProviderProps) => {
  // `config.steps` is the visible flow, derived (not stored) so it reacts to the
  // buyer's answers — branch steps appear/disappear as `visibleWhen` resolves.
  const { baseFlow, bundleSlugs } = usePremarketplaceBaseFlow();
  const { formData } = usePremarketplaceContext();
  const answers = useMemo(() => buildStepAnswers(formData ?? {}), [formData]);
  const steps = useMemo(() => computeVisibleSteps(baseFlow, answers, bundleSlugs), [baseFlow, answers, bundleSlugs]);

  const config = useMemo<ModalStepConfiguration | null>(() => (partner ? { partner, steps } : null), [partner, steps]);

  const stepManager = useStepManager(steps);

  useHiddenStepRedirect(steps);
  useStaleAnswerCleanup(steps);

  const contextValue: StepContextType = useMemo(() => ({ ...stepManager, config }), [stepManager, config]);

  return <StepContext.Provider value={contextValue}>{children}</StepContext.Provider>;
};

export const useStepContext = (): StepContextType => {
  const context = useContext(StepContext);
  if (!context) {
    throw new Error('useStepContext must be used within a StepProvider');
  }
  return context;
};
